/**
 * One organisation: what it gave, to whom, and through whom.
 *
 * The headline is deliberately four numbers rather than one. A single
 * "total" for a firm would fold together a cheque to a member's campaign,
 * the cost of a party thrown in their honour, and an uncapped donation to a
 * presidential library — three different things, only one of which is money
 * a member of Congress received.
 */

import { Link, useParams } from "react-router-dom";
import { OTHER_KINDS, api, type FirmDetail } from "../api";
import { useAsync } from "../useAsync";
import { Problem, Spinner } from "../components/bits";
import { FirmCheques } from "../components/Cheques";
import { Payees } from "../components/Payees";
import { OrganisationProfile } from "../components/WhoTheyAre";
import { lobbyistPath } from "../components/lobbyist";
import { PARTY_NAME, TINT, brief, partyKey } from "../components/party";

function Figure({
  value,
  label,
  note,
  quiet = false,
}: {
  value: string;
  label: string;
  note: string;
  quiet?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-line bg-card p-5 shadow-sm">
      <div
        className={`font-display text-2xl font-semibold tabular-nums ${quiet ? "text-muted" : ""}`}
      >
        {value}
      </div>
      <div className="mt-0.5 font-medium">{label}</div>
      <p className="mt-1.5 text-sm leading-relaxed text-muted">{note}</p>
    </div>
  );
}

/**
 * Whose money the headline figure actually was.
 *
 * "APPLE INC. — $15,300 to members of Congress" reads as a company funding
 * congressional campaigns. Every one of those cheques was written by a named
 * Apple lobbyist out of their own pocket and filed on their own LD-203, with
 * Apple on the form as their employer. Apple gave nothing, and under federal
 * law it could not have: a corporation may not contribute to a candidate.
 *
 * So the split is stated in words next to the figure rather than left to a
 * column somewhere. Roughly a sixth of the dollars in this record are filed
 * by individuals and five sixths by organisations, which makes this the
 * ordinary case in both directions rather than an edge one.
 */
function WhoseMoney({ data }: { data: FirmDetail }) {
  // A corpus written before the filer split was ingested has no `by_filer` at
  // all, and reaching through it took the whole page down to a white screen
  // over one paragraph that was going to say nothing anyway.
  const personal = data.by_filer?.lobbyist?.amount ?? 0;
  const filed = data.by_filer?.organization?.amount ?? 0;
  if (!personal && !filed) return null;
  return (
    <p className="mt-4 max-w-3xl rounded-2xl border border-line bg-sunk p-4 text-sm leading-relaxed text-muted">
      {personal > 0 && (
        <>
          <span className="font-medium text-ink">
            {filed > 0 ? `${brief(personal)} of that is not ${data.name}'s own money` : `None of that is ${data.name}'s own money`}
          </span>
          . It was filed by individual lobbyists on their own reports, as their own contributions,
          with {data.name} named on the form as their employer — a corporation may not give to a
          federal candidate at all.{" "}
        </>
      )}
      {filed > 0 && (
        <>
          {personal > 0 ? `The other ${brief(filed)} was` : `${brief(filed)} was`} filed by the
          organisation itself, which for a company means its political action committee rather than
          its treasury.
        </>
      )}
    </p>
  );
}

export function FirmPage() {
  const { registrantId } = useParams();
  const id = Number(registrantId);

  const firm = useAsync(() => api.firm(id), [id]);

  if (firm.error) return <Problem error={firm.error} retry={firm.reload} />;
  if (firm.loading || !firm.data) return <Spinner label="Reading the disclosures…" />;

  const data = firm.data;
  const parties = Object.entries(data.by_party).sort((a, b) => b[1] - a[1]);
  const partyTotal = parties.reduce((sum, [, amount]) => sum + amount, 0);

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <Link to="/money" className="text-sm text-brand hover:underline">
        ← Where the money goes
      </Link>
      <h1 className="mt-3 font-display text-3xl font-semibold">{data.name}</h1>
      <p className="mt-1.5 text-muted">
        Disclosed giving between {data.first ?? "—"} and {data.last ?? "—"}.
      </p>

      <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure
          value={brief(data.to_members)}
          label="To members of Congress"
          note={`${data.gifts.toLocaleString()} cheques reaching ${data.members.toLocaleString()} named members. This is the figure the index ranks on.`}
        />
        <Figure
          value={brief(data.elsewhere)}
          label="To everyone else"
          note={`${data.unmatched.toLocaleString()} campaign contributions filed against a party committee, a candidate who is not in Congress, or a name we could not identify.`}
          quiet
        />
        <Figure
          value={brief(data.honorary)}
          label="Events in someone's honour"
          note="The cost of an event, not a gift. The person honoured never receives this, so it is never added to what they were given."
          quiet
        />
        <Figure
          value={brief(data.other)}
          label="Inaugurals, libraries, meetings"
          note="None of it capped, and none of it to a member. Listed in full below, each with the category it was filed under."
          quiet
        />
      </div>

      <WhoseMoney data={data} />

      {/* Above the money rather than under it. A reader arriving at
          "AMERICAN ISRAEL PUBLIC AFFAIRS COMMITTEE — $2.4m" wants to know
          what that organisation is before they are told what it gave, and
          the answer is the only thing on this page that no filing states
          outright. It fetches its own payload, so a slow or failed profile
          costs the panel and not the figures. */}
      <OrganisationProfile registrantId={id} />

      {data.other_gifts.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">
            What it gave outside the contribution limits
          </h2>
          <p className="mt-1.5 max-w-3xl text-muted">
            No ceiling applies to any of these, which is why they are usually far larger than
            anything a member may accept. Dates are as filed, and a filer entering the last day of
            the reporting period is common enough that they should be read as approximate.
          </p>
          <ol className="mt-4 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
            {data.other_gifts.map((gift, index) => (
              <li
                key={`${gift.honoree}-${gift.amount}-${index}`}
                className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-4"
              >
                <span className="font-display text-lg font-semibold tabular-nums">
                  ${Math.round(gift.amount).toLocaleString()}
                </span>
                <span className="min-w-48 flex-1 font-medium">{gift.honoree}</span>
                <span className="text-sm text-faint">
                  {OTHER_KINDS[gift.kind].label} · {gift.date ?? gift.filing_year}
                </span>
                {/* The least believable figure on the site is a
                    fifty-million-dollar row here, and it is the one most
                    worth being able to open. */}
                {gift.source && (
                  <a
                    href={gift.source}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-brand hover:underline"
                  >
                    The filing
                  </a>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {partyTotal > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">Which side it funds</h2>
          <div className="mt-4 flex h-3 overflow-hidden rounded-full">
            {parties.map(([party, amount]) => (
              <div
                key={party}
                className={`${
                  partyKey(party) === "D" ? "bg-dem" : partyKey(party) === "R" ? "bg-gop" : "bg-ind"
                }`}
                style={{ width: `${(amount / partyTotal) * 100}%` }}
              />
            ))}
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {parties.map(([party, amount]) => (
              <li key={party}>
                <span className={`font-medium ${TINT[partyKey(party)]}`}>
                  {PARTY_NAME[partyKey(party)]}
                </span>{" "}
                <span className="tabular-nums text-muted">
                  {brief(amount)} · {Math.round((amount / partyTotal) * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-12 grid gap-8 lg:grid-cols-2">
        {data.lobbyists.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-semibold">Who filed it</h2>
            <p className="mt-1 text-sm text-muted">
              The named lobbyists who reported this giving. Each has a page: what they swore they
              did in government before this, and every line filed against them.
            </p>
            <ol className="mt-3 divide-y divide-line rounded-2xl border border-line bg-card">
              {data.lobbyists.slice(0, 12).map((row) => (
                <li key={row.lobbyist} className="flex items-baseline gap-3 px-4 py-2.5 text-sm">
                  <span className="w-16 font-medium tabular-nums">{brief(row.amount)}</span>
                  <Link
                    to={lobbyistPath(id, row.lobbyist)}
                    className="flex-1 font-medium text-brand hover:underline"
                  >
                    {row.lobbyist}
                  </Link>
                  <span className="text-faint">{row.gifts.toLocaleString()}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {data.payees.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-semibold">Who the cheques were written to</h2>
            <p className="mt-1 text-sm text-muted">
              The committee named on the payment, which is not always the member's own.
            </p>
            <Payees rows={data.payees.slice(0, 12)} />
          </section>
        )}
      </div>

      {data.top_members.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">Every member it funded</h2>
          <ol className="mt-4 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
            {data.top_members.map((row) => (
              <li key={row.bioguide}>
                <Link
                  to={`/member/${row.bioguide}`}
                  className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-3 transition hover:bg-sunk"
                >
                  <span className="w-20 font-medium tabular-nums">{brief(row.amount)}</span>
                  <span className="min-w-40 flex-1 font-medium">{row.full_name}</span>
                  <span className={`text-sm font-medium ${TINT[partyKey(row.party)]}`}>
                    {PARTY_NAME[partyKey(row.party)]}
                  </span>
                  <span className="text-sm text-faint">
                    {row.state} · {row.gifts.toLocaleString()} cheques · last {row.latest ?? "—"}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Every figure above is a sum of these, and none of them could be
          opened until now. That is how "$26,100 across 9 cheques" survived
          being two filings of the same report: the site had no route from a
          total to the rows it was made of. */}
      {data.gifts > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">Every cheque, and its filing</h2>
          <p className="mt-1.5 max-w-3xl text-muted">
            One row per contribution, as filed, newest first. Each links to the LD-203 it appears
            on, so any figure on this page can be read off the sworn document instead of taken on
            trust.
          </p>
          <FirmCheques registrantId={id} name={data.name} />
        </section>
      )}
    </div>
  );
}
